import { template as renderTemplate } from 'underscore';
import $ from '../../../FrontendBundle/Resources/js/jquery';
import { EventEmitter, Logger } from '../../../FrontendBundle/Resources/js/ofcs';
import CanvasInterface from './editor/canvas/interface';
import ContainerItem from './editor/item/containerItem';

/**
 * Editor prototype
 *
 *     var element = document.getElementByID('editor');
 *     var editor = new Editor(element, {
 *         showImages: false
 *     });
 *
 * @cfg {Boolean} showImages If editor should display item images
 */
export default class Editor extends EventEmitter {
    options = {
        editorUrl: '',
        saveUrl: '',
        name: 'default',

        /**
         * Contains all needed translation keys
         */
        translations: {
            loadingLayout: 'Loading layout...',
        },

        /**
         * Selector or HTML for the loading indicator
         */
        loadingIndicatorTemplate: null,

        /**
         * Loading indicator message
         */
        loadingIndicatorMessage: null,
    };

    /**
     * Triggered before the canvas is loaded.
     *
     * @event beforeload
     * @param {Editor} editor Me
     */

    /**
     * Triggered after the canvas has been loaded.
     *
     * @event load
     * @param {Editor} editor Me
     * @param {Canvas} canvas Loaded canvas
     */

    /**
     * Triggered when a canvas is set.
     *
     * @event setcanvas
     * @param {Editor} editor Me
     * @param {Canvas} canvas Canvas
     */

    /**
     * Triggered when the root item is set.
     *
     * @event setrootitem
     * @param {Editor} editor Me
     * @param {RootItem} rootItem Root item
     */

    /**
     * Triggered when a note is set.
     *
     * @event setnote
     * @param {String} note Note
     */

    /**
     * Triggered before layout is saved.
     *
     * @event beforesave
     * @param {Canvas} editor Me
     * @param {Array} data Response data
     */

    /**
     * Triggered when save is successfull.
     *
     * @event savesuccess
     * @param {Canvas} editor Me
     * @param {Array} data Response data
     */

    /**
     * Triggered when save has failed.
     *
     * @event savefailure
     * @param {Canvas} editor Me
     * @param {Array} data Response data
     */

    /**
     * @constructor
     * @param {LayoutFactory} layoutFactory Layout factory
     * @param {ItemFactory} itemFactory Item factory
     * @param {ValidationWatcher} validator Validator watcher
     * @param {LayoutUpdateTracker} layoutUpdateTracker Layout update tracker
     * @param {Object} translations Object with translations
     * @param {Context} contextManager
     * @param {Object} options Config options to configure the Editor
     */
    constructor(
        layoutFactory,
        itemFactory,
        validator,
        layoutUpdateTracker,
        translations,
        contextManager,
        options = {},
    ) {
        super();

        this.options = {
            ...this.options,
            ...options,
            translations: {
                ...this.options.translations || {},
                ...options.translations,
            },
        };

        this.layoutFactory = layoutFactory;
        this.validator = validator;
        this.layoutUpdateTracker = layoutUpdateTracker;
        this.translations = translations;
        this.contextManager = contextManager;
        this.canvas = null;
        this.note = '';
        this.rootItem = null;

        this.editorTemplate = renderTemplate(this.options.editorUrl || '');
        this.saveTemplate = renderTemplate(this.options.saveUrl || '');

        if (this.options.loadingIndicatorTemplate) {
            this.loadingIndicatorTemplate = renderTemplate(this.options.loadingIndicatorTemplate);
        }

        if (this.options.loadingIndicatorMessage) {
            this.options.translations.loadingLayout = this.options.loadingIndicatorMessage;
        }

        layoutFactory.on('beforeLoadData', () => {
            this.showLoadingIndicator(this.options.translations.loadingLayout);
        });

        layoutFactory.on('loadData', () => {
            this.removeLoadingIndicator();
        });

        this.markAsClean();

        itemFactory.on('create', (item) => {
            if (item instanceof ContainerItem) {
                item.on('change', () => this.markAsDirty());
                item.getItems().on('add change delete', () => this.markAsDirty());
            }
        });
    }

    /**
     * @return {Canvas} canvas Active canvas
     */
    getCanvas() {
        return this.canvas;
    }

    /**
     * @param {Canvas} canvas Canvas
     */
    setCanvas(canvas) {
        CanvasInterface.assert(canvas);

        this.canvas = canvas;
        this.trigger('setcanvas', this, canvas);
    }

    /**
     * @return {ContainerItem}
     */
    getRootItem() {
        return this.rootItem;
    }

    /**
     * @param {ContainerItem} rootItem
     */
    setRootItem(rootItem) {
        this.rootItem = rootItem;
        this.trigger('setrootitem', this, rootItem);
    }

    /**
     * @return {string}
     */
    getNote() {
        return this.note;
    }

    /**
     * @param {string} note
     */
    setNote(note) {
        this.note = note;
        this.markAsDirty();
        this.trigger('setnote', note);
    }

    /**
     * @return {Promise}
     */
    save() {
        const rootItem = this.getRootItem();
        const items = this._serializeItem(rootItem);
        const saveUrl = this.saveTemplate({ id: this.layoutId });
        const body = {
            note: this.getNote(),
            valid: this.isValid(),
            items: JSON.stringify(items),
        };

        this.trigger('beforesave', this, body, items);
        Logger.info('Editor.save()', body, items);

        // This needs to stay a jQuery request because jquery.alert-messages listens to all Ajax responses via event
        return new Promise((resolve, reject) => {
            $.post(saveUrl, body, ({ response }) => {
                const { success, idMap } = response ?? {};

                if (!success) {
                    this.trigger('savefailure', this, response);
                    reject(response);
                    return;
                }

                this.markAsClean();

                if (idMap) {
                    this._applyChangedIdsToItems(idMap, this.getRootItem());
                }

                if (this.layoutUpdateTracker.isAvailable()) {
                    this.layoutUpdateTracker.addUpdatedLayout(this.layoutId);
                }

                this.trigger('savesuccess', this, response);
                resolve(response);
            });
        });
    }

    /**
     *
     * @param {Object} idMap
     * @param {ItemInterface} item
     * @private
     */
    _applyChangedIdsToItems(idMap, item) {
        item.getItems().each((childItem) => {
            if (idMap[childItem.get('identifier')]) {
                // update identifier and itemId of items. prevent events (silent) to not change editors dirty flag.

                childItem.set({
                    itemId: idMap[childItem.get('identifier')].id,
                    identifier: idMap[childItem.get('identifier')].identifier,
                }, {
                    silent: true,
                });
            }

            if (childItem instanceof ContainerItem) {
                this._applyChangedIdsToItems(idMap, childItem);
            }
        });
    }

    /**
     * @param {ItemInterface} item
     * @return {Object}
     * @private
     */
    _serializeItem(item) {
        const data = item.toJSON();
        const children = [];

        if (item instanceof ContainerItem) {
            item.getItems().each((childItem) => {
                children.push(this._serializeItem(childItem));
            });
            data.children = children;
        }

        return data;
    }

    /**
     * @return {Promise}
     */
    async saveAndNotifyAboutProblems() {
        await this.save();

        if (!this.isValid()) {
            $('#alert-messages').alertMessages('add', 'warn', this.translations.warnAboutProblems);
        }
    }

    /**
     * @param {number} layoutId
     * @param {Object} wrapper
     * @param {Object} data
     */
    async loadLayout(layoutId, wrapper, data) {
        Logger.log('editor.loadLayout()', layoutId, wrapper, data);

        this.wrapper = wrapper;
        this.layoutId = layoutId;

        this.trigger('beforeload', this);

        const canvas = await this.layoutFactory.factory(layoutId, wrapper, data, this);

        this.setCanvas(canvas);
        this.markAsClean();

        this.trigger('load', this, canvas);

        this.contextManager.switchToFirstContext();

        return canvas;
    }

    isDirty() {
        return this.dirty;
    }

    markAsClean() {
        this.dirty = false;
    }

    markAsDirty() {
        this.dirty = true;
    }

    isValid() {
        return this.validator.isValid();
    }

    /**
     * @param {string} message
     */
    showLoadingIndicator(message) {
        const { wrapper } = this;

        if (!wrapper || this.loadingIndicator) {
            return;
        }

        if (this.options.loadingIndicatorTemplate) {
            this.loadingIndicator = $(this.loadingIndicatorTemplate({ message }));
            wrapper.prepend(this.loadingIndicator);
        }
    }

    removeLoadingIndicator() {
        if (this.loadingIndicator) {
            this.loadingIndicator.remove();
            delete this.loadingIndicator;
        }
    }
}
